import { API_ORIGIN } from '../config';
const API_BASE_URL = `${API_ORIGIN}/api/laptops`;

// Normalise a raw laptop object from the API into the shape the UI expects.
// Every endpoint returns the same laptop shape, so map it once here and reuse.
// empId / ownerName / ownerEmail are null when the laptop is unassigned.
const shapeLaptop = (raw) => {
  if (!raw) return null;
  return {
    laptopId: raw.laptopId,
    inventoryNumber: raw.inventoryNumber,
    empId: raw.empId ?? null,
    ownerName: raw.ownerName ?? null,
    ownerEmail: raw.ownerEmail ?? null,
    confirmedAt: raw.confirmedAt ?? null,
    confirmationSentAt: raw.confirmationSentAt ?? null,
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
  };
};

class LaptopService {
  getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }

  // Shared response handler — surfaces backend error messages.
  // Route errors use `error`; auth middleware (401/403) uses `message`.
  async handleResponse(response) {
    let data = null;
    try {
      data = await response.json();
    } catch (e) {
      // response had no JSON body
    }
    if (!response.ok) {
      const errorMessage = (data && (data.error || data.message)) || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }
    return data || {};
  }

  // GET /api/laptops — list all (filters: ?empId=, ?search=). Returns shaped laptop[].
  async getAllLaptops({ empId, search } = {}) {
    const params = new URLSearchParams();
    if (empId !== undefined && empId !== null && empId !== '') params.append('empId', empId);
    if (search) params.append('search', search);
    const query = params.toString() ? `?${params.toString()}` : '';

    const response = await fetch(`${API_BASE_URL}${query}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const data = await this.handleResponse(response);
    return (data.laptops || []).map(shapeLaptop);
  }

  // GET /api/laptops/employee/:empId — laptops belonging to one employee. Returns shaped laptop[].
  async getLaptopsByEmployee(empId) {
    const response = await fetch(`${API_BASE_URL}/employee/${empId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const data = await this.handleResponse(response);
    return (data.laptops || []).map(shapeLaptop);
  }

  // GET /api/laptops/:id — one laptop. Returns a shaped laptop.
  async getLaptop(id) {
    const response = await fetch(`${API_BASE_URL}/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const data = await this.handleResponse(response);
    return shapeLaptop(data.laptop);
  }

  // POST /api/laptops — create { inventoryNumber, empId? }. Returns the shaped laptop.
  async createLaptop({ inventoryNumber, empId }) {
    const body = { inventoryNumber };
    if (empId !== undefined) body.empId = empId;

    const response = await fetch(`${API_BASE_URL}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const data = await this.handleResponse(response);
    return shapeLaptop(data.laptop);
  }

  // PUT /api/laptops/:id — update number and/or owner (empId: null to unassign). Returns the shaped laptop.
  async updateLaptop(id, { inventoryNumber, empId }) {
    const body = {};
    if (inventoryNumber !== undefined) body.inventoryNumber = inventoryNumber;
    if (empId !== undefined) body.empId = empId; // null unassigns

    const response = await fetch(`${API_BASE_URL}/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const data = await this.handleResponse(response);
    return shapeLaptop(data.laptop);
  }

  // DELETE /api/laptops/:id — delete. Returns { success, message }.
  async deleteLaptop(id) {
    const response = await fetch(`${API_BASE_URL}/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  // POST /api/laptops/:id/send-confirmation — email this laptop's owner to confirm
  // their inventory number. Returns { success, message }.
  async sendConfirmation(id) {
    const response = await fetch(`${API_BASE_URL}/${id}/send-confirmation`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  // POST /api/laptops/send-confirmation-all — email every employee with a laptop
  // assigned. Returns { success, sentCount, failedCount, failed: [{empId, empEmail, error}] }.
  async sendConfirmationAll() {
    const response = await fetch(`${API_BASE_URL}/send-confirmation-all`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  // GET /api/laptops/my — the logged-in user's own laptop (self-service, any role).
  // 404 covers two normal, non-error states (no employee match / no laptop assigned),
  // so this resolves to { laptop: null, notFoundMessage } instead of throwing for those.
  async getMyLaptop() {
    const response = await fetch(`${API_BASE_URL}/my`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    if (response.status === 404) {
      let data = null;
      try {
        data = await response.json();
      } catch (e) {
        // response had no JSON body
      }
      const notFoundMessage = (data && (data.error || data.message)) || 'No laptop is assigned to you';
      return { laptop: null, notFoundMessage };
    }
    const data = await this.handleResponse(response);
    return { laptop: shapeLaptop(data.laptop), notFoundMessage: null };
  }

  // PUT /api/laptops/my — correct the inventory number on the logged-in user's own
  // laptop; the backend confirms it automatically. Throws with the backend's message
  // on 400/404/409 (including the "already confirmed" lock) for the caller to handle.
  async updateMyLaptop(inventoryNumber) {
    const response = await fetch(`${API_BASE_URL}/my`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ inventoryNumber }),
    });
    const data = await this.handleResponse(response);
    return shapeLaptop(data.laptop);
  }
}

export default new LaptopService();
